/**
 * Portal API clients.
 *
 * Two clients, deliberately separate:
 *
 *  - `publicApi` — the anonymous, credential-free client the shell needs
 *    before sign-in (today: the public branding endpoint). `withCredentials`
 *    is false so a stray HttpOnly cookie from another EduViaOS subdomain can
 *    never leak into a cold-load read.
 *
 *  - `api` — the AUTHENTICATED client (Phase SC-3b). Built on the shared
 *    `createApiClient`, it holds the access token in memory only, replays
 *    the HttpOnly `eos_refresh` cookie on `/auth/refresh`, and transparently
 *    retries a single 401. `withCredentials: true` is mandatory so the
 *    refresh cookie survives a reload. Tenant scope travels via the
 *    host-derived `X-Tenant-Slug` header (the edge Worker overwrites it, so
 *    a forged header can't escape its own subdomain).
 *
 * `onUnauthorized` is wired post-construction by the SessionProvider so a
 * dead refresh chain flushes UI state back to the login screen.
 */
import axios, { type AxiosInstance } from "axios";
import { createApiClient, getCurrentTenant } from "@eduviaos/shared";

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export const publicApi: AxiosInstance = axios.create({
  baseURL,
  // Anonymous surface — never send credentials.
  withCredentials: false,
  headers: {
    "Cache-Control": "no-cache",
    "X-Eduviaos-Surface": "portal",
  },
  timeout: 15_000,
});

publicApi.interceptors.request.use((config) => {
  const slug = getCurrentTenant().slug;
  if (slug) {
    config.headers = config.headers ?? {};
    config.headers["X-Tenant-Slug"] = slug;
  }
  return config;
});

/**
 * Registered by the SessionProvider so a dead refresh chain (expired /
 * revoked cookie) flushes UI state back to the login screen. Stored in a
 * module ref because the shared client binds `onUnauthorized` once at
 * construction — this indirection lets React wire the live handler after.
 */
let unauthorizedHandler: (() => void) | null = null;

/** Wire the "session is really gone" callback (SessionProvider effect). */
export function onSessionExpired(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * Authenticated portal client. Singleton — the tenant slug is resolved
 * lazily on every request so the same SPA works on any per-tenant host
 * without re-instantiation. The `X-Eduviaos-Surface` header tags portal
 * traffic for BE observability parity with `publicApi`.
 */
export const api = createApiClient({
  baseURL,
  tenantSlugProvider: () => getCurrentTenant().slug,
  onUnauthorized: () => unauthorizedHandler?.(),
});

api.raw.defaults.headers.common["X-Eduviaos-Surface"] = "portal";
